import { useCallback, useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  getETaxAutoSendRounds,
  previewETaxAutoSendRound,
  runETaxAutoSendRound,
} from "../../../services";
import {
  EtaxAutoSendDryRunResponse,
  EtaxAutoSendRound,
} from "../../../types/etax-auto-send.type";
import { numberWithCommas } from "../../../utils";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";
import ETaxPagination from "../Pagination";
import RoundsTable from "./RoundsTable";
import RunRoundPreviewDialog from "./RunRoundPreviewDialog";

const compact = "@media " + COMPACT_QUERY;
const LIMIT = 10;
/** How often the list re-reads itself while the newest round is still sending. */
export const RUNNING_POLL_INTERVAL_MS = 10000;

/**
 * The auto-send round history, with "run a round now".
 *
 * A real run returns as soon as the round is RUNNING and keeps sending in the background, so
 * while the newest round on the first page is RUNNING the list re-reads itself every ~10s
 * (quietly, without the skeleton) until it settles. The same applies when the page is opened
 * during a scheduled round. Polling stops on unmount.
 */
export default function AutoSendRounds() {
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(LIMIT);
  const [rows, setRows] = useState<EtaxAutoSendRound[]>([]);
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const [previewing, setPreviewing] = useState<boolean>(false);
  const [preview, setPreview] = useState<EtaxAutoSendDryRunResponse | null>(null);
  const [running, setRunning] = useState<boolean>(false);

  // Only the newest request may write state — a slow poll must not repaint over a page change.
  const requestSeq = useRef<number>(0);

  const fetchRounds = useCallback(
    async (silent = false) => {
      const seq = requestSeq.current + 1;
      requestSeq.current = seq;

      if (!silent) setLoading(true);
      try {
        const {
          data: { data },
        } = await getETaxAutoSendRounds(page, limit);
        if (requestSeq.current !== seq) return;
        setRows(data.rows);
        setCount(data.count);
      } catch {
        // Surfaced by the axios interceptor. A failed quiet poll keeps what is on screen.
        if (requestSeq.current !== seq || silent) return;
        setRows([]);
        setCount(0);
      } finally {
        // Also on a quiet poll: it may have superseded a loud request still showing the skeleton.
        if (requestSeq.current === seq) setLoading(false);
      }
    },
    [page, limit],
  );

  useEffect(() => {
    fetchRounds();
  }, [fetchRounds]);

  const newestRunning = page === 1 && rows[0]?.status === "RUNNING";

  useEffect(() => {
    if (!newestRunning) return undefined;
    const timer = window.setTimeout(() => fetchRounds(true), RUNNING_POLL_INTERVAL_MS);
    return () => window.clearTimeout(timer);
    // `rows` is a dependency so every completed poll schedules the next one.
  }, [newestRunning, rows, fetchRounds]);

  const handlePreview = async () => {
    if (previewing) return;
    setPreviewing(true);
    try {
      const {
        data: { data },
      } = await previewETaxAutoSendRound();
      setPreview(data);
    } catch {
      // Surfaced by the axios interceptor.
    } finally {
      setPreviewing(false);
    }
  };

  const showNewestRound = () => {
    if (page === 1) {
      fetchRounds();
    } else {
      // Changing the page refetches on its own.
      setPage(1);
    }
  };

  const handleRun = async () => {
    if (running) return;
    setRunning(true);
    try {
      await runETaxAutoSendRound();
      setPreview(null);
      showNewestRound();
    } catch (error) {
      // The toast is the interceptor's (409 → มีรอบกำลังทำงานอยู่). A running round is
      // already in the list, so show it rather than leave a preview that can no longer run.
      if ((error as AxiosError).response?.status === 409) {
        setPreview(null);
        showNewestRound();
      }
    } finally {
      setRunning(false);
    }
  };

  return (
    <Paper>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        sx={{ p: 2, [compact]: { p: 1.5, flexWrap: "wrap" } }}
      >
        <Typography variant="body2" color="text.secondary">
          ทั้งหมด{" "}
          <Box component="span" fontWeight={700} color="text.primary">
            {numberWithCommas(count)}
          </Box>{" "}
          รอบ
          {newestRunning && " · กำลังส่ง ระบบจะอัปเดตรายการให้อัตโนมัติ"}
        </Typography>
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          onClick={handlePreview}
          loading={previewing}
        >
          รันรอบตอนนี้
        </Button>
      </Box>

      <RoundsTable rows={rows} loading={loading} limit={limit} />

      <ETaxPagination
        totalCount={count}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />

      <RunRoundPreviewDialog
        preview={preview}
        loading={running}
        onClose={() => setPreview(null)}
        onConfirm={handleRun}
      />
    </Paper>
  );
}
